import { useCallback, useEffect, useRef, useState } from "react";
import { demoApi, problemMessage } from "@/api/client";
import { useSelectedEntity } from "@/hooks/useSelectedEntity";
import type {
  AddAnimalWeightRequest,
  Animal,
  AnimalGroup,
  AnimalStatusChange,
  AnimalWeight,
  BulkCreateAnimalsRequest,
  BulkCreateAnimalsResponse,
  CalfCropRequest,
  CalfCropResponse,
  ChangeAnimalStatusRequest,
  CreateAnimalRequest,
  CreatePurchaseRequest,
  CreateSaleRequest,
  Guid,
  Herd,
  LivestockPostingResponse,
  UpdateAnimalRequest,
} from "@/api/types";

export type LivestockStatus = "loading" | "ready" | "error";

/** ef-048-2b — a fresh `Idempotency-Key` per submit, so a double-tapped Buy/Sell button
 *  replays the ORIGINAL response instead of posting twice. Same fallback-for-older-iOS-
 *  Safari shape `outbox.ts`'s own `newClientId` already documents; this key never leaves
 *  the device except as the header itself, so the (much smaller) collision odds on the
 *  fallback path are an acceptable trade against a hard runtime error. */
function newIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * The widest single page this screen ever asks for — `LivestockEndpoints.
 * MaxAnimalsPageSize` (200). ef-042-0c — a roster past this ceiling no longer gets
 * silently truncated: `loadMore` below fetches subsequent pages at the same width,
 * offset by how many animals are already held, until a page comes back short of the
 * ceiling (the server's own "there is no more" signal — no separate total exists on this
 * read-only array response).
 */
const ANIMALS_PAGE_LIMIT = 200;

export interface UseLivestockResult {
  /** `null` until resolved and on failure. ACTIVE herds only — what `groupByHerd` and
   *  `AnimalDialog`'s own herd picker both need; an archived herd is not a place a new or
   *  existing animal should be filed under. See `archivedHerds` for the rest. */
  herds: Herd[] | null;
  /** ef-042-0b — always `[]` rather than `null`: the archived disclosure has a real,
   *  always-valid answer ("nothing archived yet") even while `herds` is still loading or
   *  failed. Same convention `usePlaces`' own `archived` field uses. */
  archivedHerds: Herd[];
  groups: AnimalGroup[] | null;
  animals: Animal[] | null;
  /**
   * ef-047-1 — the tenant's SELECTED entity, read straight off the shared, persisted
   * switcher (`useSelectedEntity.ts`) rather than this hook's own `entities[0]` guess.
   * `null` while loading, or if the tenant genuinely has no active entity, in which case
   * creating an animal is blocked rather than sent with a guessed id.
   */
  entityId: Guid | null;
  status: LivestockStatus;
  error: string | null;
  /** ef-042-0c — true once the most recently fetched page (the first load, or the most
   *  recent `loadMore`) came back exactly `ANIMALS_PAGE_LIMIT` wide, i.e. there may be
   *  more animals past what's loaded. A failed `loadMore` leaves this untouched, so the
   *  "Load more" control stays offered for a retry rather than looking like the end of
   *  the roster. */
  hasMore: boolean;
  /** True only while a `loadMore` fetch is in flight — never true during the initial
   *  `load` (that's covered by `status === "loading"`). */
  loadingMore: boolean;
  /** Set by a failed `loadMore`; cleared at the start of the next attempt. Independent of
   *  `error`, which is the whole-roster load's own failure surface. */
  loadMoreError: string | null;
  /** Fetches the next `ANIMALS_PAGE_LIMIT` animals at `offset = animals.length` and
   *  appends them. A no-op while a previous call is still in flight or once `hasMore` is
   *  false — guarded by a ref (not just the `loadingMore` state) so two clicks fired
   *  before the first render lands still only ever produce one follow-up request. */
  loadMore: () => Promise<void>;
  reload: () => Promise<void>;
  /** Throws on failure — Livestock.tsx catches this itself to read the server's own
   *  field errors off it (`problemFieldErrors`), which this hook has no opinion about. */
  createAnimal: (body: CreateAnimalRequest) => Promise<Animal>;
  updateAnimal: (id: Guid, body: UpdateAnimalRequest) => Promise<Animal>;
  deleteAnimal: (id: Guid) => Promise<void>;
  /** ef-042-0b — throws on failure; a re-archive (already archived, or gone) is a 404,
   *  read directly off `ApiError.status` by the caller — see
   *  `db/migrations/0043_herd_archive.sql`'s header for why this is not idempotent. */
  archiveHerd: (id: Guid) => Promise<void>;
  /** ef-042-0b — idempotent server-side; throws only on a genuine failure (cross-tenant
   *  or unknown id). */
  restoreHerd: (id: Guid) => Promise<void>;
  /** Throws on failure — Livestock.tsx reads `ApiError.status === 409` off it directly to
   *  show "Already {status}." rather than the server's generic conflict sentence. */
  changeAnimalStatus: (id: Guid, body: ChangeAnimalStatusRequest) => Promise<void>;
  /** Lazily fetched, on demand, when an animal's own detail view opens — never held in
   *  this hook's own state, so a farmer viewing one animal's history never pays for
   *  fetching everyone else's. */
  getAnimalHistory: (id: Guid) => Promise<AnimalStatusChange[]>;
  /** ef-059-7 — one animal's dated weighings, newest `weighedOn` first. Lazy, like the history. */
  getAnimalWeights: (id: Guid) => Promise<AnimalWeight[]>;
  /** ef-059-7 — throws on failure (a second weighing on one day is a 409, a bad date a 400).
   *  Reloads the roster on success so the row's "current weight" follows the latest-dated one. */
  addAnimalWeight: (id: Guid, body: AddAnimalWeightRequest) => Promise<AnimalWeight>;
  /** ef-041-6 — throws on failure, same convention as `createAnimal`; `LivestockBatchDialog`
   *  reads `problemMessage`/`problemFieldErrors` off it itself. Reloads the roster on
   *  success so the newly-purchased animals appear without a manual refresh. */
  postPurchase: (body: CreatePurchaseRequest) => Promise<LivestockPostingResponse>;
  /** ef-041-6 — throws on failure (a 409 means one or more animals raced to non-active);
   *  reloads the roster on success so a sold animal's status flips without a manual
   *  refresh. */
  postSale: (body: CreateSaleRequest) => Promise<LivestockPostingResponse>;
  /** ef-059-8 — all-or-nothing bulk create. `idempotencyKey` is minted ONCE per dialog open by
   *  the caller (a double-clicked Save replays); a second same-tick call returns the first call's
   *  in-flight promise. Throws on failure (a 400 names `animals[i].field`); reloads on success. */
  createAnimalsBulk: (body: BulkCreateAnimalsRequest, idempotencyKey: string) => Promise<BulkCreateAnimalsResponse>;
  /** ef-059-8 — one dam, one day, 1-4 calves; same key + in-flight contract as above. */
  createCalfCrop: (body: CalfCropRequest, idempotencyKey: string) => Promise<CalfCropResponse>;
}

/**
 * ef-041-5 — the owner's Livestock screen: herds, groups and the animal roster,
 * `GET /api/demo/livestock/{herds,groups,animals}`.
 *
 * Owner-only server-side (403 for an employee login). This hook does not check that and
 * must not: a client-side role check is a rendering decision, and treating it as the
 * control is how the real one stops being maintained — same reasoning `useSchedule`/
 * `usePlaces` give. The Livestock screen this feeds is already behind the owner shell.
 *
 * Re-reads herds/groups/animals WHOLESALE after every write rather than patching arrays
 * in place, same convention `useSchedule` uses and for the same reason: derived headcounts
 * (Livestock.tsx's own `useMemo`) must never drift from what the server actually holds.
 */
export function useLivestock(): UseLivestockResult {
  // ef-047-1 — the shared, persisted switcher; replaces this hook's own `entities[0]`
  // guess. Herds/groups/animals below were never entity-filtered by `fetchAll` in the
  // first place (`getHerds(undefined, true)` passes no entity id), so `entityId` is
  // purely an output of this hook now, not an input any fetch below depends on — no
  // gating on `entityStatus` needed the way `Fields.tsx`/`Grain.tsx` require it.
  const { entityId } = useSelectedEntity();
  // Holds BOTH active and archived — split into `herds`/`archivedHerds` below, same
  // one-GET-split-client-side convention `usePlaces` uses for `active`/`archived`.
  const [allHerds, setAllHerds] = useState<Herd[] | null>(null);
  const [groups, setGroups] = useState<AnimalGroup[] | null>(null);
  const [animals, setAnimals] = useState<Animal[] | null>(null);
  const [status, setStatus] = useState<LivestockStatus>("loading");
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);
  // Guards `loadMore` against a double click that lands before React re-renders the
  // `disabled` button from `loadingMore` — plain, synchronously-set, never read for
  // rendering (that's `loadingMore`'s job).
  const loadingMoreRef = useRef(false);
  // Dedupes the whole-roster fetch itself. `request()` in api/client.ts calls `fetch()`
  // as its own first statement, so `Promise.all([demoApi.getHerds(), ...])` below
  // dispatches all three requests SYNCHRONOUSLY the moment `load` runs — before React's
  // StrictMode dev double-invoke (mount → cleanup → mount, same tick) has any chance to
  // `abort()` the first one. Without this, the initial mount fires two real network
  // round-trips; the second observably "wins" (the first's `signal.aborted` check below
  // discards its own result), which is silently harmless against the live API but not
  // against anything that answers differently per call. `fetchAllRef` makes a second,
  // overlapping `load()` piggyback on the in-flight request instead of starting a new
  // one; each caller still independently decides whether to apply the result via its own
  // `signal`.
  const fetchAllRef = useRef<Promise<{
    herdList: Herd[];
    groupList: AnimalGroup[];
    animalList: Animal[];
  }> | null>(null);

  // ef-042-0b — `includeArchived: true` so `allHerds` (split below into `herds` /
  // `archivedHerds`) actually carries the archived side; NOT given a `signal` — this
  // promise is shared across overlapping `load()` callers (see the remark above), so
  // aborting it for one caller would silently starve the others still waiting on it.
  // Each caller instead applies its own `signal` check AFTER this resolves, below.
  const fetchAll = useCallback(() => {
    if (fetchAllRef.current) return fetchAllRef.current;
    const promise = Promise.all([
      demoApi.getHerds(undefined, true),
      demoApi.getAnimalGroups(),
      demoApi.getAnimals({ limit: ANIMALS_PAGE_LIMIT }),
    ])
      .then(([herdList, groupList, animalList]) => ({ herdList, groupList, animalList }))
      .finally(() => {
        fetchAllRef.current = null;
      });
    fetchAllRef.current = promise;
    return promise;
  }, []);

  const load = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      try {
        const { herdList, groupList, animalList } = await fetchAll();
        if (signal?.aborted) return;
        setAllHerds(herdList);
        setGroups(groupList);
        setAnimals(animalList);
        setHasMore(animalList.length === ANIMALS_PAGE_LIMIT);
        setLoadMoreError(null);
        setStatus("ready");
        setError(null);
      } catch (err) {
        if (signal?.aborted || (err instanceof DOMException && err.name === "AbortError")) return;
        setAllHerds(null);
        setGroups(null);
        setAnimals(null);
        setHasMore(false);
        setStatus("error");
        setError(problemMessage(err, "Could not load the livestock roster."));
      }
    },
    [fetchAll],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const reload = useCallback(() => load(), [load]);

  const loadMore = useCallback(async (): Promise<void> => {
    if (loadingMoreRef.current || !hasMore) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    setLoadMoreError(null);
    try {
      const offset = animals?.length ?? 0;
      const next = await demoApi.getAnimals({ limit: ANIMALS_PAGE_LIMIT, offset });
      setAnimals((prev) => [...(prev ?? []), ...next]);
      setHasMore(next.length === ANIMALS_PAGE_LIMIT);
    } catch (err) {
      // `hasMore` is deliberately left as-is — a failed follow-up page is not "the end
      // of the roster", it's a retry candidate, so the control stays offered.
      setLoadMoreError(problemMessage(err, "Could not load more animals."));
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [animals, hasMore]);

  const createAnimal = useCallback(
    async (body: CreateAnimalRequest): Promise<Animal> => {
      const animal = await demoApi.createAnimal(body);
      await load();
      return animal;
    },
    [load],
  );

  const updateAnimal = useCallback(
    async (id: Guid, body: UpdateAnimalRequest): Promise<Animal> => {
      const animal = await demoApi.updateAnimal(id, body);
      await load();
      return animal;
    },
    [load],
  );

  const deleteAnimal = useCallback(
    async (id: Guid): Promise<void> => {
      await demoApi.deleteAnimal(id);
      await load();
    },
    [load],
  );

  const archiveHerd = useCallback(
    async (id: Guid): Promise<void> => {
      await demoApi.archiveHerd(id);
      await load();
    },
    [load],
  );

  const restoreHerd = useCallback(
    async (id: Guid): Promise<void> => {
      await demoApi.restoreHerd(id);
      await load();
    },
    [load],
  );

  const changeAnimalStatus = useCallback(
    async (id: Guid, body: ChangeAnimalStatusRequest): Promise<void> => {
      await demoApi.changeAnimalStatus(id, body);
      await load();
    },
    [load],
  );

  const getAnimalHistory = useCallback(
    (id: Guid): Promise<AnimalStatusChange[]> => demoApi.getAnimalStatusHistory(id),
    [],
  );

  const getAnimalWeights = useCallback((id: Guid): Promise<AnimalWeight[]> => demoApi.getAnimalWeights(id), []);

  const addAnimalWeight = useCallback(
    async (id: Guid, body: AddAnimalWeightRequest): Promise<AnimalWeight> => {
      const weight = await demoApi.addAnimalWeight(id, body);
      await load();
      return weight;
    },
    [load],
  );

  // ef-049-0a — synchronous, ref-based in-flight guards, same shape as `loadingMoreRef`
  // above: checked and set as the very first statement, cleared in `finally`, so a
  // SECOND same-tick click never mints a new Idempotency-Key or calls the API, no matter
  // whether React has re-rendered the `disabled` attribute yet. Both functions return
  // `Promise<LivestockPostingResponse>` (unlike a `Promise<void>` handler), so the guard
  // holds the FIRST call's own in-flight promise rather than a bare boolean — a same-tick
  // second call awaits and resolves to that identical result instead of racing a second
  // network request, same in-flight-dedup idiom `fetchAllRef` above already uses.
  const postPurchaseRef = useRef<Promise<LivestockPostingResponse> | null>(null);
  const postSaleRef = useRef<Promise<LivestockPostingResponse> | null>(null);

  const postPurchase = useCallback(
    (body: CreatePurchaseRequest): Promise<LivestockPostingResponse> => {
      if (postPurchaseRef.current) return postPurchaseRef.current;
      const inFlight = (async () => {
        try {
          const posted = await demoApi.postLivestockPurchase(body, newIdempotencyKey());
          await load();
          return posted;
        } finally {
          postPurchaseRef.current = null;
        }
      })();
      postPurchaseRef.current = inFlight;
      return inFlight;
    },
    [load],
  );

  const postSale = useCallback(
    (body: CreateSaleRequest): Promise<LivestockPostingResponse> => {
      if (postSaleRef.current) return postSaleRef.current;
      const inFlight = (async () => {
        try {
          const posted = await demoApi.postLivestockSale(body, newIdempotencyKey());
          await load();
          return posted;
        } finally {
          postSaleRef.current = null;
        }
      })();
      postSaleRef.current = inFlight;
      return inFlight;
    },
    [load],
  );

  // ef-059-8 — same synchronous in-flight guard as postPurchase/postSale above.
  const bulkRef = useRef<Promise<BulkCreateAnimalsResponse> | null>(null);
  const calfCropRef = useRef<Promise<CalfCropResponse> | null>(null);

  const createAnimalsBulk = useCallback(
    (body: BulkCreateAnimalsRequest, idempotencyKey: string): Promise<BulkCreateAnimalsResponse> => {
      if (bulkRef.current) return bulkRef.current;
      const inFlight = (async () => {
        try {
          const created = await demoApi.createAnimalsBulk(body, idempotencyKey);
          await load();
          return created;
        } finally {
          bulkRef.current = null;
        }
      })();
      bulkRef.current = inFlight;
      return inFlight;
    },
    [load],
  );

  const createCalfCrop = useCallback(
    (body: CalfCropRequest, idempotencyKey: string): Promise<CalfCropResponse> => {
      if (calfCropRef.current) return calfCropRef.current;
      const inFlight = (async () => {
        try {
          const created = await demoApi.createCalfCrop(body, idempotencyKey);
          await load();
          return created;
        } finally {
          calfCropRef.current = null;
        }
      })();
      calfCropRef.current = inFlight;
      return inFlight;
    },
    [load],
  );

  const herds = allHerds === null ? null : allHerds.filter((h) => h.archivedAt === null);
  const archivedHerds = allHerds === null ? [] : allHerds.filter((h) => h.archivedAt !== null);

  return {
    herds,
    archivedHerds,
    groups,
    animals,
    entityId,
    status,
    error,
    hasMore,
    loadingMore,
    loadMoreError,
    loadMore,
    reload,
    createAnimal,
    updateAnimal,
    deleteAnimal,
    archiveHerd,
    restoreHerd,
    changeAnimalStatus,
    getAnimalHistory,
    getAnimalWeights,
    addAnimalWeight,
    postPurchase,
    postSale,
    createAnimalsBulk,
    createCalfCrop,
  };
}
